import { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useGetFdaProcesses } from 'api/services/processesService';
import { ContentHeader } from '../../layout/ContentHeader';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { DarwinFlex, DarwinSpin } from 'components/darwin-ui';
import { SidebarProcessMap } from './SidebarProcessMap';
import {
  useChangeStatusProcessMapById,
  useCreateCanvasProcessMap,
  useGetCanvasProcessMapById,
} from 'api/services/processMapService';
import { useUI } from 'contexts/UiContext';
import { ErrorType } from 'types/genericTypes';
import { extractQueryData } from 'utils/services';
import { IconName } from 'components/darwin-ui/Icon';
import { OnConnectParams, Process } from 'types/processesTypes';
import styles from './ProcessMap.module.css';
import { ProcessLifeCycleStatus } from 'constants/process-lifecycle-status.constants';

import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  MarkerType,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { CustomNode } from './CustomNode';
import { ActionButtonsBar } from './ActionButtonsBar';
import { SelfLoopEdge } from './SelfLoopEdge';
import { DefaultEdge } from './DefaultEdge';
import {
  NodeResponse,
  parseEdges,
  parseNodes,
  transformFlowData,
} from './helpers';
import { OutputCellNode, outputCellNodeStyles } from './OutputCellNode';
import { ROUTES } from 'routes/routes';
import { NODE_HEIGHT, NODE_WIDTH } from 'constants/processMaps';

export enum NodeTypes {
  NORMAL = 'normal',
  OUTPUT_INPUT_CELL = 'output_input_cell',
}

export type NodesTypeData = {
  newNodeId?: string;
  processId?: string;
  processName?: string;
  isEndOfFlow?: boolean;
  isInitOfFlow?: boolean;
  processDescription?: string | null;
  processIcon?: IconName | string;
  processStatus?: string;
  assignedUserCount?: number;
  processMapId?: string;
  isSaved?: boolean;
  outputCellValue?: number;
  onDelete?: (nodeId: string) => void;
  connectedTarget?: string;
  connectedSource?: string;
  outputCellCount?: string;
  type?: NodeTypes;
  isDragging?: boolean;
};

export type NodesType = {
  id: string;
  position: {
    x: number;
    y: number;
  };
  data: NodesTypeData;
  type: string;
  style?: { [key: string]: string | number };
};

export type EdgesType = {
  id: string;
  source: string;
  target: string;
  type?: string;
};

// react flow requires to define customNode, edgeTypes outside the component
const nodeTypes = {
  customNode: CustomNode,
  intermediateNode: OutputCellNode,
};

const edgeTypes = {
  selfLoopEdge: SelfLoopEdge,
  defaultEdge: DefaultEdge,
};

export const EditProcessMap = () => {
  const { id: processMapId } = useParams();
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const navigate = useNavigate();
  const { selectedCompany } = useUI();
  const { screenToFlowPosition, getViewport } = useReactFlow();
  const reactFlowWrapperRef = useRef<HTMLDivElement | null>(null);

  // get category and process data
  const { data: categories, isError } = useGetFdaProcesses();

  useEffect(() => {
    if (isError) {
      openNotification(t('error'), t('data-tables.categories-error'), 'error');
    }
  }, [isError, t, openNotification]);

  // react flow logic
  const [nodes, setNodes, onNodesChange] = useNodesState<NodesType>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<EdgesType>([]);

  // delete node
  const handleDeleteNode = useCallback(
    (nodeId: string) => {
      setNodes(prevNodes => {
        // check if the node is an intermediate node
        const intermediates = prevNodes.filter(
          node =>
            node.type === 'intermediateNode' &&
            (node.data.connectedSource === nodeId ||
              node.data.connectedTarget === nodeId)
        );

        // check if the node is an output cell
        const hasTransaction = intermediates.some(
          node => !!node.data.outputCellValue
        );

        // If there are transactions in the output cell, show a warning
        // and do not delete the node
        if (hasTransaction) {
          openNotification(
            t('warning'),
            t('process-map.delete-process.can-not-delete'),
            'warning',
            'alert'
          );
          return prevNodes;
        }

        // nodes ids to delete, including intermediates
        // and the node itself
        // we use a set to avoid duplicates
        const nodeIdsToDelete = new Set([
          nodeId,
          ...intermediates.map(node => node.id),
        ]);

        // delete the node and its edges
        // we use filter to avoid duplicates
        const updatedNodes = prevNodes.filter(
          node => !nodeIdsToDelete.has(node.id)
        );

        // delete the edges connected to the node
        setEdges(prevEdges =>
          prevEdges.filter(
            edge =>
              !nodeIdsToDelete.has(edge.source) &&
              !nodeIdsToDelete.has(edge.target)
          )
        );

        return updatedNodes;
      });
    },
    [setNodes, setEdges, openNotification, t]
  );

  // callback called when user connects two nodes
  const onConnect = useCallback(
    (params: OnConnectParams) => {
      const isSelfLoop = params.source === params.target;

      if (isSelfLoop) {
        setEdges(prevEdges => [
          ...prevEdges,
          {
            id: uuidv4(),
            source: params.source,
            target: params.target,
            type: 'selfLoopEdge',
            markerEnd: { type: MarkerType.ArrowClosed },
            style: { strokeWidth: 2 },
            data: {
              onDelete: (id: string) => {
                setEdges(edges => edges.filter(edge => edge.id !== id));
              },
              type: NodeTypes.NORMAL,
            },
          },
        ]);
        return;
      }

      setNodes(prevNodes => {
        const updatedNodes = [...prevNodes];

        // check if there is an intermediate node for source (fan-out)
        let intermediateNode = updatedNodes.find(
          node =>
            node.type === 'intermediateNode' &&
            node.data.connectedSource === params.source
        );

        // if there isn't an intermediate node for source, check if there is one for the TARGET (fan-in)
        if (!intermediateNode) {
          intermediateNode = updatedNodes.find(
            node =>
              node.type === 'intermediateNode' &&
              node.data.connectedTarget === params.target
          );
        }

        // if there is no intermediate node, create one
        if (!intermediateNode) {
          const sourceNode = updatedNodes.find(n => n.id === params.source);
          const targetNode = updatedNodes.find(n => n.id === params.target);

          if (!sourceNode || !targetNode) return prevNodes;

          const intermediateNodeId = uuidv4();

          const intermediatePosition = {
            x: (sourceNode.position.x + targetNode.position.x) / 2 + 180,
            y: (sourceNode.position.y + targetNode.position.y) / 2,
          };

          intermediateNode = {
            id: intermediateNodeId,
            position: intermediatePosition,
            type: 'intermediateNode',
            data: {
              newNodeId: intermediateNodeId,
              connectedSource: params.source, // fan-out
              connectedTarget: params.target, // fan-in
              outputCellValue: 0,
              processId: sourceNode.data.processId, // to identify intermediate node
              processName: 'outputCellProcessName',
              processDescription: 'outputCellProcessDescription',
              processIcon: 'outputCellProcessIcon',
              processStatus: 'outputCellProcessStatus',
              assignedUserCount: 0,
              processMapId: processMapId || '',
              isSaved: false,
              type: NodeTypes.OUTPUT_INPUT_CELL,
            },
            style: outputCellNodeStyles,
          };

          updatedNodes.push(intermediateNode);
        }

        // now work with edges avoiding duplicates
        setEdges(prevEdges => {
          const newEdges = [...prevEdges];

          // source ➡️ intermediate
          const edgeFromSourceExists = newEdges.some(
            e => e.source === params.source && e.target === intermediateNode!.id
          );

          // intermediate ➡️ target
          const edgeFromIntermediateExists = newEdges.some(
            e => e.source === intermediateNode!.id && e.target === params.target
          );

          if (!edgeFromSourceExists) {
            newEdges.push({
              id: uuidv4(),
              source: params.source,
              target: intermediateNode!.id,
              type: 'defaultEdge',
            });
          }

          if (!edgeFromIntermediateExists) {
            newEdges.push({
              id: uuidv4(),
              source: intermediateNode!.id,
              target: params.target,
              type: 'defaultEdge',
            });
          }

          return newEdges;
        });

        return updatedNodes;
      });
    },
    [processMapId, setEdges, setNodes]
  );

  // add process to canvas
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const data = event.dataTransfer.getData('application/reactflow');
      if (!data) return;

      const process: Process = JSON.parse(data);

      const flowPosition = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // Calculate the position of the node based on the flow position
      // and the width and height of the node
      // We center the node in the flow position
      const position = {
        x: flowPosition.x - NODE_WIDTH / 2,
        y: flowPosition.y - NODE_HEIGHT / 2,
      };

      const newNodeId = uuidv4();

      setNodes(prevNodes => [
        ...prevNodes,
        {
          id: newNodeId,
          position,
          data: {
            newNodeId,
            processId: process.id,
            processName: process.name,
            processDescription: process.description,
            processIcon: process.icon || '',
            processStatus: ProcessLifeCycleStatus.INACTIVE,
            assignedUserCount: 0,
            processMapId: processMapId || '',
            isSaved: false,
            onDelete: () => handleDeleteNode(newNodeId),
            type: NodeTypes.NORMAL,
          },
          type: 'customNode',
        },
      ]);
    },
    [setNodes, processMapId, handleDeleteNode, screenToFlowPosition]
  );

  const handleProcessClick = useCallback(
    (process: Process) => {
      if (!reactFlowWrapperRef.current) return;

      const bounds = reactFlowWrapperRef.current.getBoundingClientRect();
      const { x, y, zoom } = getViewport();

      const position = {
        x: (bounds.width / 2 - x) / zoom - NODE_WIDTH / 2,
        y: (bounds.height / 2 - y) / zoom - NODE_HEIGHT / 2,
      };

      setNodes(prevNodes => {
        const newNodeId = uuidv4();

        return [
          ...prevNodes,
          {
            id: newNodeId,
            position: position,
            data: {
              newNodeId,
              processId: process.id,
              processName: process.name,
              processDescription: process.description,
              processIcon: process.icon || '',
              processStatus: ProcessLifeCycleStatus.INACTIVE,
              assignedUserCount: 0,
              processMapId: processMapId || '',
              isSaved: false,
              onDelete: () => handleDeleteNode(newNodeId),
              type: NodeTypes.NORMAL,
            },
            type: 'customNode',
          },
        ];
      });
    },
    [handleDeleteNode, processMapId, setNodes, getViewport, reactFlowWrapperRef]
  );

  const onDragOver = useCallback((event: any) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // handle get initial canvas
  const canvasProcessMapByIdQuery = useGetCanvasProcessMapById(
    selectedCompany || '',
    processMapId || ''
  );

  const canvasProcessMapByIdData = extractQueryData(
    canvasProcessMapByIdQuery,
    undefined
  );

  useEffect(() => {
    if (canvasProcessMapByIdData) {
      const { nodes, edges } = canvasProcessMapByIdData;

      const parsedNodes = parseNodes(
        nodes,
        edges,
        handleDeleteNode,
        processMapId || ''
      );
      const parsedEdges = parseEdges(edges, setEdges);

      setNodes(parsedNodes);
      setEdges(parsedEdges);

      const endOfFlows = nodes?.filter(
        (node: NodeResponse) =>
          node.isEndOfFlow === true && node.type === 'normal'
      );

      const outputCellEndFlow = nodes?.find(
        (node: NodeResponse) =>
          node.isEndOfFlow === true && node.type === 'output_input_cell'
      );

      //Delete the output cell if it is end of flow
      if (endOfFlows?.length === 0 && outputCellEndFlow) {
        if (outputCellEndFlow.outputCellCount > 0) {
          setNodes(prevNodes =>
            prevNodes.map((node: any) => {
              return node.id === outputCellEndFlow.nodeId
                ? {
                    ...node,
                    data: { ...node.data, isEndOfFlow: false },
                  }
                : node;
            })
          );
        } else {
          setNodes(prevNodes => {
            const newNodes = prevNodes.filter(
              (node: any) => node.id !== outputCellEndFlow.nodeId
            );

            setEdges(prevEdges =>
              prevEdges.filter(edge => edge.target !== outputCellEndFlow.nodeId)
            );

            return newNodes;
          });
        }
      }

      if (endOfFlows && endOfFlows.length > 0) {
        endOfFlows.forEach((endOfFlow: any) => {
          const intermediateNodeId = uuidv4();
          const newEdgeId = uuidv4();

          const intermediatePosition = {
            x: endOfFlow.positionX + 140,
            y: endOfFlow.positionY + 160,
          };

          const newOutPutCellNode = {
            id: intermediateNodeId,
            position: intermediatePosition,
            type: 'intermediateNode',
            data: {
              newNodeId: endOfFlow.nodeId,
              isEndOfFlow: true,
              outputCellValue: endOfFlow.outputCellCount || 0,
              connectedSource: endOfFlow.nodeId,
              connectedTarget: '',
              processId: endOfFlow.processId,
              processName: 'outputCellProcessName',
              processDescription: 'outputCellProcessDescription',
              processIcon: 'outputCellProcessIcon',
              processStatus: 'outputCellProcessStatus',
              assignedUserCount: 0,
              processMapId: processMapId || '',
              isSaved: false,
              type: NodeTypes.OUTPUT_INPUT_CELL,
            },
            style: outputCellNodeStyles,
          };

          const newEdge = {
            id: newEdgeId,
            source: endOfFlow.nodeId,
            target: newOutPutCellNode.id,
            type: 'defaultEdge',
          };

          const edgeConnected = edges.find(
            (edge: any) => edge.sourceNodeId === endOfFlow.nodeId
          );

          if (edgeConnected) {
            const nodeConnectedToEdge = nodes.find(
              (node: NodeResponse) => node.nodeId === edgeConnected.targetNodeId
            );

            if (nodeConnectedToEdge?.isEndOfFlow === true) {
              return; // Skip this one
            }

            if (
              nodeConnectedToEdge?.outputCellCount > 0 &&
              nodeConnectedToEdge?.isEndOfFlow === false
            ) {
              setNodes(prevNodes =>
                prevNodes.map((node: any) =>
                  node.id === nodeConnectedToEdge.nodeId
                    ? { ...node, isEndOfFlow: true }
                    : node
                )
              );
            }
          }

          if (!edgeConnected) {
            setNodes(prevNodes => {
              const nodeAlreadyExists = prevNodes.some(
                node => node.id === newOutPutCellNode.id
              );

              if (nodeAlreadyExists) {
                return prevNodes;
              }

              const newNodes = [...prevNodes, newOutPutCellNode];

              setEdges(prevEdges => {
                const edgeAlreadyExists = prevEdges.some(
                  edge => edge.id === newEdge.id
                );

                if (edgeAlreadyExists) {
                  return prevEdges;
                }

                return [...prevEdges, newEdge];
              });

              return newNodes;
            });
          }
        });
      }
    }
  }, [
    canvasProcessMapByIdData,
    handleDeleteNode,
    processMapId,
    setEdges,
    setNodes,
  ]);

  // handle create/edit canvas and update process map status
  const processMapStatusValueRef = useRef<string>('');

  const {
    mutateAsync: changeStatusProcessMap,
    isPending: isPendingChangeStatusProcessMap,
  } = useChangeStatusProcessMapById();

  const {
    mutateAsync: createCanvasProcessMap,
    isPending: isCreateCanvasProcessMapPending,
  } = useCreateCanvasProcessMap();

  const handleCreateCanvas = async () => {
    const nodesAndEdgesFormattedData = transformFlowData(nodes, edges);
    try {
      // update process map status if status changed
      if (
        processMapStatusValueRef.current &&
        processMapStatusValueRef.current !==
          canvasProcessMapByIdData?.processMapStatus
      ) {
        await changeStatusProcessMap({
          payload: { status: processMapStatusValueRef.current },
          companyId: selectedCompany || '',
          traceabilityId: processMapId || '',
        });
      }

      // create/edit canvas itself
      await createCanvasProcessMap({
        payload: nodesAndEdgesFormattedData,
        companyId: selectedCompany || '',
        planId: processMapId || '',
      });

      openNotification(
        t('success'),
        t('create-canvas-process-map.create-success'),
        'success'
      );
    } catch (err) {
      const apiError = err as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(
          t('error'),
          t('create-canvas-process-map.create-error'),
          'error'
        );
      }
    }
  };

  const refetchCanvas = useCallback(() => {
    canvasProcessMapByIdQuery.refetch();
  }, [canvasProcessMapByIdQuery]);

  const handleDisableButtons =
    isCreateCanvasProcessMapPending || isPendingChangeStatusProcessMap;

  return (
    <>
      <DarwinSpin spinning={canvasProcessMapByIdQuery.isLoading} fullscreen />

      <ContentHeader
        title={canvasProcessMapByIdData?.processMapName || t('new-process')}
        onCancel={() => navigate(ROUTES.PROCESSES.MAPS.LIST)}
        onSave={handleCreateCanvas}
        saveDisabled={handleDisableButtons}
      />

      {/* action buttons bar */}
      <ActionButtonsBar
        processMapStatus={
          canvasProcessMapByIdData?.processMapStatus ||
          ProcessLifeCycleStatus.INACTIVE
        }
        processMapStatusValueRef={processMapStatusValueRef}
        refetchCanvas={refetchCanvas}
      />

      {/* sidebar */}
      <DarwinFlex className={styles.reactFlowFlexContainer}>
        <SidebarProcessMap
          categories={categories?.data || []}
          onProcessClick={handleProcessClick}
        />

        {/* react flow */}
        <div
          ref={reactFlowWrapperRef}
          className={styles.reactFlowSectionContainer}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeDragStart={(_, node) => {
              setNodes(prevNodes =>
                prevNodes.map(n =>
                  n.id === node.id
                    ? { ...n, data: { ...n.data, isDragging: true } }
                    : n
                )
              );
            }}
            onNodeDragStop={(_, node) => {
              setNodes(prevNodes =>
                prevNodes.map(n =>
                  n.id === node.id
                    ? { ...n, data: { ...n.data, isDragging: false } }
                    : n
                )
              );
            }}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
          >
            <Controls />
            <MiniMap />
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          </ReactFlow>
        </div>
      </DarwinFlex>
    </>
  );
};
